import { APITypesV1 } from "@cur8/api-client";
import { Measurement } from "@cur8/rich-entity";

export type DimensionConfig = {
  name: string;
  unit: string;
};

// Named conversion for display-only imperial units.
// The API always stores SI values; these conversions are display-only.
// "ft_in" and "st_lbs" are compound (two input fields → one SI value).
export type ImperialConversion = "lbs" | "ft_in" | "st_lbs";

// Row label for a sub-metric of a MetricGroup, shown in GroupTile.
export type PositionalVariant = {
  type: "Position";
  value: "Left" | "Right";
};

export type Variant = PositionalVariant;

export type MetricConfig = {
  metricName: string;
  label: string;
  dimensions: DimensionConfig[];
  generation: "all" | "gen1";
  autoImported?: boolean;
  // Set on sub-metrics of a MetricGroup, used as the row label in GroupTile.
  variant?: Variant;
  // Maps country codes (e.g. "GB", "US") and/or "default" to a conversion type.
  // Only metrics with a single dimension should use this.
  imperial?: Partial<Record<string, ImperialConversion>>;
  // Per-country override for whether the imperial conversion is shown by
  // default. Countries not listed here fall back to the GB/US convention
  // below (imperial by default) — set this only where a metric's default
  // deviates from that convention.
  preferImperialByCountry?: Partial<Record<string, boolean>>;
};

export function getImperialConversion(
  config: MetricConfig,
  countryCode: string | undefined
): ImperialConversion | null {
  if (!config.imperial) {
    return null;
  }
  if (countryCode && config.imperial[countryCode]) {
    return config.imperial[countryCode]!;
  }
  return config.imperial["default"] ?? null;
}

export function getPreferImperial(
  config: MetricConfig,
  countryCode: string | undefined
): boolean {
  if (!countryCode) {
    return false;
  }
  const override = config.preferImperialByCountry?.[countryCode];
  if (override != null) {
    return override;
  }
  return countryCode === "GB" || countryCode === "US";
}

export type MetricGroup = {
  label: string;
  metrics: MetricConfig[];
  generation: "all" | "gen1";
};

export type GridItem = MetricConfig | MetricGroup;

export function isGroup(item: GridItem): item is MetricGroup {
  return "metrics" in item;
}

// Individual metric configs
const heightConfig: MetricConfig = {
  metricName: "body.height",
  label: "Height",
  dimensions: [{ name: "value", unit: "cm" }],
  generation: "all",
  imperial: { GB: "ft_in", US: "ft_in" },
};

const weightConfig: MetricConfig = {
  metricName: "body.weight",
  label: "Weight",
  dimensions: [{ name: "value", unit: "kg" }],
  generation: "all",
  // GB reads imperial weight as stone + lbs, US as lbs. Keep the countries
  // listed: getImperialConversion falls back to imperial["default"], so a
  // "default" key would give SE and every new market an imperial toggle.
  imperial: { GB: "st_lbs", US: "lbs" },
  // GB defaults to kg for weight specifically (unlike height's ft/in default).
  preferImperialByCountry: { GB: false },
};

const saturationConfig: MetricConfig = {
  metricName: "cardio.oxygen_saturation",
  label: "Saturation",
  dimensions: [{ name: "value", unit: "%" }],
  generation: "all",
};

const Left: PositionalVariant = { type: "Position", value: "Left" };
const Right: PositionalVariant = { type: "Position", value: "Right" };

const gripLeftConfig: MetricConfig = {
  metricName: "body.grip_strength.left",
  label: "Grip left",
  variant: Left,
  dimensions: [{ name: "value", unit: "kg" }],
  generation: "all",
  imperial: { US: "lbs" },
};

const gripRightConfig: MetricConfig = {
  metricName: "body.grip_strength.right",
  label: "Grip right",
  variant: Right,
  dimensions: [{ name: "value", unit: "kg" }],
  generation: "all",
  imperial: { US: "lbs" },
};

const eyePressureLeftConfig: MetricConfig = {
  metricName: "risk_assessment.eye_pressure.left",
  label: "Eye pressure L",
  variant: Left,
  dimensions: [{ name: "value", unit: "mm Hg" }],
  generation: "all",
};

const eyePressureRightConfig: MetricConfig = {
  metricName: "risk_assessment.eye_pressure.right",
  label: "Eye pressure R",
  variant: Right,
  dimensions: [{ name: "value", unit: "mm Hg" }],
  generation: "all",
};

const brachialLeftConfig: MetricConfig = {
  metricName: "cardio.brachial_pressure.left",
  label: "BP left arm",
  variant: Left,
  dimensions: [
    { name: "systolic", unit: "mm Hg" },
    { name: "diastolic", unit: "mm Hg" },
  ],
  generation: "all",
  autoImported: true,
};

const brachialRightConfig: MetricConfig = {
  metricName: "cardio.brachial_pressure.right",
  label: "BP right arm",
  variant: Right,
  dimensions: [
    { name: "systolic", unit: "mm Hg" },
    { name: "diastolic", unit: "mm Hg" },
  ],
  generation: "all",
  autoImported: true,
};

const ankleLeftConfig: MetricConfig = {
  metricName: "cardio.ankle_pressure.left",
  label: "Ankle pressure L",
  variant: Left,
  dimensions: [{ name: "value", unit: "mm Hg" }],
  generation: "all",
  autoImported: true,
};

const ankleRightConfig: MetricConfig = {
  metricName: "cardio.ankle_pressure.right",
  label: "Ankle pressure R",
  variant: Right,
  dimensions: [{ name: "value", unit: "mm Hg" }],
  generation: "all",
  autoImported: true,
};

const ssnFemoralConfig: MetricConfig = {
  metricName: "cardio.distance.ssn_femoralis.left",
  label: "SSN-femoral",
  dimensions: [{ name: "value", unit: "cm" }],
  generation: "gen1",
};

// Groups
const gripGroup: MetricGroup = {
  label: "Grip strength",
  metrics: [gripRightConfig, gripLeftConfig],
  generation: "all",
};

export const eyePressureGroup: MetricGroup = {
  label: "Eye pressures",
  metrics: [eyePressureRightConfig, eyePressureLeftConfig],
  generation: "all",
};

// Exported so the side-to-side thresholds in measurementCaution.ts can be
// tested against the metric names actually configured here.
export const brachialGroup: MetricGroup = {
  label: "Brachial blood pressures",
  metrics: [brachialRightConfig, brachialLeftConfig],
  generation: "all",
};

export const ankleGroup: MetricGroup = {
  label: "Ankle blood pressures",
  metrics: [ankleRightConfig, ankleLeftConfig],
  generation: "all",
};

// Flat list of all individual metrics (for query keys, risk ranges, chip count)
const ALL_METRICS: MetricConfig[] = [
  heightConfig,
  weightConfig,
  saturationConfig,
  gripLeftConfig,
  gripRightConfig,
  eyePressureLeftConfig,
  eyePressureRightConfig,
  brachialLeftConfig,
  brachialRightConfig,
  ankleLeftConfig,
  ankleRightConfig,
  ssnFemoralConfig,
];

// Grid layout with groups
const GRID_ITEMS: GridItem[] = [
  weightConfig,
  heightConfig,
  gripGroup,
  eyePressureGroup,
  saturationConfig,
  ssnFemoralConfig,
  brachialGroup,
  ankleGroup,
];

export function getMeasurementConfigs(
  generation: APITypesV1.RoomGeneration | undefined
): MetricConfig[] {
  if (generation === APITypesV1.RoomGeneration.Gen1) {
    return ALL_METRICS;
  }
  return ALL_METRICS.filter((m) => m.generation === "all");
}

export function getMeasurementGrid(
  generation: APITypesV1.RoomGeneration | undefined
): GridItem[] {
  if (generation === APITypesV1.RoomGeneration.Gen1) {
    return GRID_ITEMS;
  }
  return GRID_ITEMS.filter((item) => item.generation === "all");
}

export const CONCIERGE_GENERATOR = "cur8:app:concierge";

export const WEARS_CONTACT_LENSES_METRIC_NAME =
  "risk_assessment.eye_pressure.wears_contact_lenses";

export const WEARS_CONTACT_LENSES_LABEL =
  "Is the member wearing hard contact lenses today?";

// Decodes the same boolean-as-0/1 dimension DocUI writes/reads. Returns
// undefined when never answered, so callers can tell "not asked yet" apart
// from an explicit "no".
export function getWearsContactLenses(
  measurements: Measurement[] | undefined
): boolean | undefined {
  const value = measurements
    ?.find((m) => m.metricName === WEARS_CONTACT_LENSES_METRIC_NAME)
    ?.dimensions.find((d) => d.name === "value")?.value;
  if (value === 1) {
    return true;
  }
  if (value === 0) {
    return false;
  }
  return undefined;
}

export function getRequiredMeasurementConfigs(
  configs: MetricConfig[],
  wearsContactLenses: boolean
): MetricConfig[] {
  if (!wearsContactLenses) {
    return configs;
  }
  const skippedMetricNames = new Set(
    eyePressureGroup.metrics.map((m) => m.metricName)
  );
  return configs.filter((c) => !skippedMetricNames.has(c.metricName));
}
